import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { DpEmployeeListItem, listEmployeesApi } from "../../dp/api/employeesApi";
import BulkStoreAssignmentModal from "../../dp/components/BulkStoreAssignmentModal";
import { DP_EMPLOYEE_PAGE } from "../../dp/routes";
import { AdminUserListItem, listUsersApi } from "../../admin-sistema/api/adminUsersApi";
import { ADMIN_USUARIOS_PAGE } from "../../admin-sistema/routes";
import { SUPERVISOR_TYPES, USER_TYPE_OPTIONS } from "../../admin-sistema/components/userAccess";

type Props = {
  token: string;
  storeId: string;
  storeName: string;
  /** Colaboradores lotados: ADMIN e DP (módulo DP). */
  showStaff: boolean;
  /** Supervisores vinculados: só o ADMIN (a lista de usuários é da Administração). */
  showSupervisors: boolean;
};

/**
 * Aba "Equipe" da ficha da loja: os colaboradores CLT lotados (com "Lotar ou
 * retirar", que valia só em DP → Cadastros) e os supervisores com a loja
 * vinculada — as do supervisor de campo entram na cobertura semanal. O vínculo
 * do supervisor continua sendo editado em Administração → Gestão de usuários.
 */
export default function StoreTeamTab({ token, storeId, storeName, showStaff, showSupervisors }: Props) {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<DpEmployeeListItem[] | null>(null);
  const [supervisors, setSupervisors] = useState<AdminUserListItem[] | null>(null);
  const [assigning, setAssigning] = useState(false);

  const loadStaff = useCallback(async () => {
    if (!showStaff) return;
    try {
      const page = await listEmployeesApi(token, { storeId, limit: 100 });
      setStaff(page.items.filter((e) => e.status !== "DESLIGADO"));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os colaboradores da loja.");
      setStaff([]);
    }
  }, [token, storeId, showStaff]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    if (!showSupervisors) return;
    listUsersApi(token, { storeId, limit: 100 })
      .then((page) => setSupervisors(page.items.filter((u) => SUPERVISOR_TYPES.includes(u.userType))))
      .catch(() => setSupervisors([]));
  }, [token, storeId, showSupervisors]);

  return (
    <div className="storeTeam">
      {showStaff ? (
        <section className="storeTeam__block" aria-labelledby="storeTeam-staff">
          <header className="storeTeam__head">
            <h3 id="storeTeam-staff" className="dpSection__title">
              Colaboradores lotados {staff ? <span className="storeTeam__count">{staff.length}</span> : null}
            </h3>
            <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--primary" onClick={() => setAssigning(true)}>
              Lotar ou retirar
            </button>
          </header>
          {staff === null ? (
            <div className="dpEmpty">Carregando...</div>
          ) : staff.length === 0 ? (
            <div className="dpEmpty">Nenhum colaborador lotado nesta loja.</div>
          ) : (
            <ul className="storeTeam__list">
              {staff.map((e) => (
                <li key={e.id}>
                  <button type="button" className="storeTeam__row" onClick={() => navigate(DP_EMPLOYEE_PAGE(e.id))}>
                    <span className="storeTeam__name">{e.socialName || e.fullName}</span>
                    <span className="storeTeam__meta">{e.status === "ATIVO" ? "Ativo" : e.status.charAt(0) + e.status.slice(1).toLowerCase()}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {showSupervisors ? (
        <section className="storeTeam__block" aria-labelledby="storeTeam-sup">
          <header className="storeTeam__head">
            <h3 id="storeTeam-sup" className="dpSection__title">
              Supervisores vinculados {supervisors ? <span className="storeTeam__count">{supervisors.length}</span> : null}
            </h3>
            <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => navigate(ADMIN_USUARIOS_PAGE())}>
              Gestão de usuários
            </button>
          </header>
          <p className="dpFormHint">
            O vínculo se edita no usuário do supervisor. As lojas vinculadas ao supervisor de campo entram na cobertura semanal
            de visitas.
          </p>
          {supervisors === null ? (
            <div className="dpEmpty">Carregando...</div>
          ) : supervisors.length === 0 ? (
            <div className="dpEmpty">Nenhum supervisor vinculado a esta loja.</div>
          ) : (
            <ul className="storeTeam__list">
              {supervisors.map((u) => (
                <li key={u.id} className="storeTeam__row storeTeam__row--static">
                  <span className="storeTeam__name">{u.nomeCompleto}</span>
                  <span className="storeTeam__meta">{USER_TYPE_OPTIONS.find((o) => o.value === u.userType)?.label ?? u.userType}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {assigning ? (
        <BulkStoreAssignmentModal
          token={token}
          storeId={storeId}
          storeName={storeName}
          onClose={() => setAssigning(false)}
          onDone={() => {
            setAssigning(false);
            loadStaff();
          }}
        />
      ) : null}
    </div>
  );
}
